import { formatDate, formatDecimal, KanbanContractError } from '../contract';
import { ufCode } from '../text';

export const CANDIDATES_PER_PAGE = 25;
export const SITUATIONS = [
  { value: 'ALL', label: 'Ambos' },
  { value: 'EMPTY', label: 'Vazios' },
  { value: 'IN_TRANSIT', label: 'Em viagem' },
];
export const canReadCandidates = (permission) =>
  ['VISUALIZAR_CARGAS', 'VISUALIZAR_VEICULOS', 'VISUALIZAR_IMPLEMENTO', 'VISUALIZAR_VIAGENS'].every(
    permission,
  );
const missing = 'Não informado';
const integer = (value) => Number.isSafeInteger(value) && value >= 0;
const id = (value) => integer(value) && value > 0;
const text = (value) => value == null || typeof value === 'string';
const textObject = (value, fields) =>
  value == null ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    fields.every((field) => text(value[field])));
const date = (value) => value == null || formatDate(value) !== missing;
const measure = (value) =>
  value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0);
const links = {
  FROTA: 'Frota',
  OURO: 'Ouro',
  AGREGADO_CARRETA_COOPER: 'Agregado Carreta Cooper',
  AGREGADO_CONJUNTO_COMPLETO: 'Agregado Conjunto Completo',
  TERCEIRO: 'Terceiro',
};
/** Vínculo da placa sem o detalhe de corredor (listas do Kanban, 5668). */
export const linkTypeLabel = (type) => links[type] || 'Não informado';
export function linkLabel(row) {
  const label = links[row.linkType] || 'Vínculo não informado';
  return row.linkType === 'AGREGADO_CARRETA_COOPER'
    ? `${label} · ${row.corridor ? 'Corredor' : 'Normal'}`
    : label;
}
export const statusLabel = (row) =>
  ({ EMPTY: 'Vazio', IN_TRANSIT: 'Em viagem' })[row.status] || 'Não informado';
export const platesLabel = (row) =>
  [row.vehiclePlate, ...row.wagons.map((wagon) => wagon.plate || 'Carreta não informada')]
    .filter(Boolean)
    .join(' / ') || missing;
export function bodyLabel(row) {
  const assets = row.wagons.length ? row.wagons : [row.vehicle];
  const bodies = [
    ...new Set(assets.map((asset) => asset.bodyTypeName || 'Carroceria não informada')),
  ].join(' / ');
  return `${bodies} | ${row.axes == null ? 'Eixos não informados' : `${row.axes} eixos`}`;
}
// 5653: só a sigla da UF; estado desconhecido aparece como veio.
const place = (point) =>
  [point?.city, point?.province && (ufCode(point.province) || point.province)]
    .filter(Boolean)
    .join('/') || missing;
export function routeLabel(row) {
  if (row.status === 'EMPTY') return place(row.geographicReference);
  if (row.status !== 'IN_TRANSIT') return missing;
  const route = row.routeContext;
  const cc = route?.costCenterName || (route?.costCenterId ? String(route.costCenterId) : missing);
  return `${place(route?.origin)} → ${place(route?.destination)} | ${cc}`;
}
const km = (meters) =>
  meters == null ? missing : `${formatDecimal(Number((meters / 1000).toFixed(2)))} km`;
/** KM até Entrega (posição → entrega da viagem atual) | KM até Coleta (entrega ou posição → coleta). */
export function distanceLabel(row) {
  if (row.status === 'UNKNOWN') return missing;
  const pickup = km(row.route.radiusDistanceMeters);
  return row.status === 'EMPTY'
    ? `— | ${pickup}`
    : `${km(row.route.remainingDeliveryMeters)} | ${pickup}`;
}
const WINDOWS = {
  GUARANTEED_AWAITING_OPENING: 'Garantido · aguarda abertura',
  GUARANTEED: 'Garantido',
  POSSIBLE: 'Possível',
};
/** Janela de carregamento: garantido quando até a chegada máxima cabe no limite da coleta. */
export const windowLabel = (row) => WINDOWS[row.route.window?.status] || missing;
/**
 * Em viagem: janela agendada da última descarga da viagem (início → fim; no mesmo dia, o fim
 * mostra só a hora). Vazio: a disponibilidade atual.
 */
export function availabilityLabel(row) {
  if (row.status === 'IN_TRANSIT' && row.scheduledDeliveryStartAt && row.scheduledDeliveryAt) {
    const start = formatDate(row.scheduledDeliveryStartAt);
    const end = formatDate(row.scheduledDeliveryAt);
    return `${start} → ${end.slice(0, 10) === start.slice(0, 10) ? end.slice(11) : end}`;
  }
  return formatDate(row.availableAt);
}
const loadingWindow = (window, route) =>
  !!window &&
  typeof window === 'object' &&
  Object.hasOwn(WINDOWS, window.status) &&
  date(window.earliestArrivalAt) &&
  date(window.latestArrivalAt) &&
  formatDate(window.earliestArrivalAt) !== missing &&
  formatDate(window.latestArrivalAt) !== missing &&
  Date.parse(window.earliestArrivalAt) <= Date.parse(window.latestArrivalAt) &&
  window.earliestArrivalAt === route.estimatedPickupAt &&
  date(window.loadingStartAt) &&
  formatDate(window.loadingLimitAt) !== missing &&
  Date.parse(window.earliestArrivalAt) <= Date.parse(window.loadingLimitAt) &&
  (window.status === 'POSSIBLE') ===
    Date.parse(window.latestArrivalAt) > Date.parse(window.loadingLimitAt) &&
  [window.slackMinHours, window.slackMaxHours].every(
    (value) => typeof value === 'number' && Number.isFinite(value),
  );
/** Telefone do motorista vinculado ao veículo, no mesmo formato do contato do Kanban. */
/** Captura do veículo utilizada na avaliação de elegibilidade. */
export function vehiclePositionLabel(row) {
  const position = row.position;
  if (!position || position.assetType !== 'VEHICLE' || position.assetId !== row.vehicleId)
    return missing;
  return formatDate(position.capturedAt);
}
export function candidateBlock(row) {
  return row.evaluation === 'INCOMPLETE'
    ? 'INCOMPLETE'
    : row.refusal.active
      ? 'REFUSED'
      : 'ELIGIBLE';
}
const INCOMPLETE_REASONS = {
  LINK_UNCLASSIFIED: 'Vínculo não classificado',
  DRIVER_UNKNOWN: 'Sem motorista',
  PLATE_IDENTITY_UNKNOWN: 'Placa sem identificação',
  SHARED_PLATE_CONFLICT: 'Placa em outro conjunto',
  // Mesma mensagem que o Aceitar devolve: a saída é inativar o cadastro duplicado.
  DUPLICATE_PLATE_REGISTRATION:
    'Identidade da placa compartilhada. Atualize o cadastro antes de atender.',
  COMPOSITION_CONFLICT: 'Viagem de outra empresa',
};
/** Motivo pelo qual o veículo listado não pode ser atendido nem recusado. */
export const incompleteLabel = (row) =>
  row.issues
    .filter((issue) => Object.hasOwn(INCOMPLETE_REASONS, issue))
    .map((issue) => INCOMPLETE_REASONS[issue])
    .join(' · ') || 'Cadastro incompleto';
const latitude = (value) =>
  typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 90;
const longitude = (value) =>
  typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 180;
/** Mapa: coleta com coordenadas e uma posição por veículo da lista (todas as páginas). */
function mapOverview(data) {
  const pickup = data.pickup;
  const points = data.mapPoints;
  if (
    !pickup ||
    !id(pickup.id) ||
    !latitude(pickup.lat) ||
    !longitude(pickup.lng) ||
    !text(pickup.city) ||
    !text(pickup.province) ||
    !Array.isArray(points) ||
    points.length !== data.totalCount
  )
    return false;
  const seen = new Set();
  return points.every((p) => {
    const valid =
      p &&
      id(p.vehicleId) &&
      p.candidateId === `vehicle:${p.vehicleId}` &&
      !seen.has(p.candidateId) &&
      text(p.vehiclePlate) &&
      ['EMPTY', 'IN_TRANSIT', 'UNKNOWN'].includes(p.status) &&
      ['ELIGIBLE', 'INCOMPLETE'].includes(p.evaluation) &&
      typeof p.refused === 'boolean' &&
      ((p.lat === null && p.lng === null) || (latitude(p.lat) && longitude(p.lng)));
    seen.add(p.candidateId);
    return valid;
  });
}
export function validateCandidates(data, item, requested) {
  const fail = () => {
    throw new KanbanContractError('Resposta inválida de veículos. Atualize a consulta.');
  };
  const pagination = data?.pagination;
  const universe = data?.operationalUniverse;
  if (
    !data ||
    data.loadId !== item.loadId ||
    data.loadKind !== item.type ||
    data.scope !== 'ROAD_RADIUS' ||
    formatDate(data.evaluatedAt) === missing ||
    data.radiusKm !== requested.radiusKm ||
    !['totalCount', 'compatibleCount', 'eligibleCount', 'incompleteCount', 'pendingCount'].every(
      (key) => integer(data[key]),
    ) ||
    data.pendingCount !== 0 ||
    data.totalCount !== data.eligibleCount + data.incompleteCount ||
    data.compatibleCount < data.eligibleCount ||
    data.compatibleCount > data.totalCount ||
    pagination?.page !== requested.page ||
    pagination.perPage !== requested.perPage ||
    pagination.radiusKm !== requested.radiusKm ||
    pagination.status !== requested.status ||
    pagination.totalPages !== Math.ceil(data.totalCount / requested.perPage) ||
    pagination.hasMore !== requested.page * requested.perPage < data.totalCount ||
    universe?.radiusKm !== 500 ||
    !['COMPLETE', 'PARTIAL', 'ERROR'].includes(universe.state) ||
    !['eligibleCount', 'refusedCount', 'pendingCount'].every((key) => integer(universe[key])) ||
    universe.refusedCount > universe.eligibleCount ||
    universe.eligibleCount < data.eligibleCount ||
    universe.pendingCount < data.pendingCount ||
    (universe.state === 'COMPLETE' && universe.pendingCount !== 0) ||
    typeof universe.canAttendWithOther !== 'boolean' ||
    (universe.canAttendWithOther &&
      (universe.state !== 'COMPLETE' ||
        universe.pendingCount !== 0 ||
        universe.refusedCount !== universe.eligibleCount)) ||
    !mapOverview(data) ||
    !Array.isArray(data.data) ||
    data.data.length !==
      Math.min(
        requested.perPage,
        Math.max(0, data.totalCount - (requested.page - 1) * requested.perPage),
      )
  )
    fail();
  const seen = new Set();
  let lastBlock = -1;
  data.data.forEach((row) => {
    if (
      !row ||
      !id(row.vehicleId) ||
      row.candidateId !== `vehicle:${row.vehicleId}` ||
      seen.has(row.candidateId) ||
      !text(row.vehiclePlate) ||
      !row.vehicle ||
      row.vehicle.id !== row.vehicleId ||
      !text(row.vehicle.bodyTypeName) ||
      !Array.isArray(row.wagons) ||
      !row.wagons.every((w) => w && id(w.id) && text(w.plate) && text(w.bodyTypeName)) ||
      !(row.linkType === null || Object.hasOwn(links, row.linkType)) ||
      typeof row.corridor !== 'boolean' ||
      !(row.priorityRank === null || (integer(row.priorityRank) && row.priorityRank <= 5)) ||
      !(row.axes === null || integer(row.axes)) ||
      !textObject(row.driver, ['name', 'phone']) ||
      !textObject(row.geographicReference, ['city', 'province']) ||
      !textObject(row.routeContext, ['costCenterName']) ||
      !textObject(row.routeContext?.origin, ['city', 'province']) ||
      !textObject(row.routeContext?.destination, ['city', 'province']) ||
      !(row.routeContext?.costCenterId == null || id(row.routeContext.costCenterId)) ||
      !(
        row.wagonPosition == null ||
        (typeof row.wagonPosition === 'object' &&
          id(row.wagonPosition.assetId) &&
          date(row.wagonPosition.capturedAt))
      ) ||
      !['EMPTY', 'IN_TRANSIT', 'UNKNOWN'].includes(row.status) ||
      (requested.status !== 'ALL' && row.status !== requested.status) ||
      !['ELIGIBLE', 'INCOMPLETE'].includes(row.evaluation) ||
      !['COMPATIBLE', 'PENDING'].includes(row.profileEvaluation) ||
      (row.evaluation === 'INCOMPLETE' &&
        (row.profileEvaluation !== 'PENDING' || !row.issues?.length)) ||
      !Array.isArray(row.issues) ||
      !row.issues.every((i) => typeof i === 'string') ||
      !Object.hasOwn(row, 'scheduledDeliveryStartAt') ||
      ![
        'availableAt',
        'estimatedDeliveryAt',
        'scheduledDeliveryStartAt',
        'scheduledDeliveryAt',
      ].every((key) => date(row[key])) ||
      !row.route ||
      !['CALCULATED', 'MISSING_DATA', 'ERROR', 'NO_ROUTE'].includes(row.route.state) ||
      !measure(row.route.totalToPickupMeters) ||
      !measure(row.route.radiusDistanceMeters) ||
      row.route.radiusDistanceMeters === null ||
      row.route.radiusDistanceMeters > requested.radiusKm * 1000 ||
      row.route.canReachPickupOnTime !== true ||
      !row.route.estimatedPickupAt ||
      !date(row.route.estimatedPickupAt) ||
      row.route.provider !== 'STRAIGHT_LINE' ||
      !loadingWindow(row.route.window, row.route) ||
      row.position?.source !== 'PLATE_LOCATION' ||
      row.position?.assetType !== 'VEHICLE' ||
      row.position?.assetId !== row.vehicleId ||
      row.position?.validity !== 'RECENT_24H' ||
      !row.position?.capturedAt ||
      !date(row.position.capturedAt) ||
      Date.parse(row.position.capturedAt) > Date.parse(data.evaluatedAt) ||
      Date.parse(row.position.capturedAt) < Date.parse(data.evaluatedAt) - 86400000 ||
      !measure(row.route.remainingDeliveryMeters) ||
      row.route.withinRadius === false ||
      !row.refusal ||
      typeof row.refusal.active !== 'boolean' ||
      !Array.isArray(row.refusal.eventIds) ||
      !row.refusal.eventIds.every(id) ||
      row.refusal.active !== row.refusal.eventIds.length > 0 ||
      (row.evaluation === 'ELIGIBLE' &&
        (row.status === 'UNKNOWN' ||
          row.linkType === null ||
          row.priorityRank === null ||
          row.profileEvaluation !== 'COMPATIBLE' ||
          row.route.state !== 'CALCULATED' ||
          row.route.totalToPickupMeters === null ||
          row.route.withinRadius !== true ||
          row.route.withinOperationalRadius !== true))
    )
      fail();
    seen.add(row.candidateId);
    const block = ['ELIGIBLE', 'REFUSED', 'INCOMPLETE'].indexOf(candidateBlock(row));
    if (block < lastBlock) fail();
    lastBlock = block;
  });
  return data;
}
